import { QuantityEscalationItem } from '../reducers/messagingSlice';

/**
 * HITL-1621: display helpers for the Unresolved Items panel.
 *
 * The producer routes most unmatched items down the invalid-option lane as raw
 * machine tokens (76% of production invalid-option cards), e.g.
 * `not_on_menu_butterfinger_burger` or `coupon_not_on_menu`. These are detected
 * and re-presented as human-readable not-on-menu / coupon cards instead of
 * being rendered verbatim.
 */
const NOT_ON_MENU_TOKEN_PREFIX = 'not_on_menu_';
const COUPON_NOT_ON_MENU_TOKEN = 'coupon_not_on_menu';

const isNotOnMenuToken = (option: string): boolean =>
  option.startsWith(NOT_ON_MENU_TOKEN_PREFIX);

const isCouponToken = (option: string): boolean =>
  option === COUPON_NOT_ON_MENU_TOKEN;

/**
 * Turn a producer item string into something an agent can read: strip the
 * `not_on_menu_` prefix when present, replace underscores with spaces, and
 * title-case each word. Numbers and symbols pass through untouched
 * (`not_on_menu_5.99_one` → "5.99 One").
 */
const prettifyUnresolvedItemName = (raw: string): string => {
  const withoutPrefix = raw.startsWith(NOT_ON_MENU_TOKEN_PREFIX)
    ? raw.slice(NOT_ON_MENU_TOKEN_PREFIX.length)
    : raw;
  return withoutPrefix
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
};

/** Case/whitespace-insensitive key for joining producer item names to cart item names. */
const normalizeItemName = (name: string): string => name.trim().toLowerCase();

export interface CappedQuantity {
  originalQuantity: number;
  cappedTo: number;
}

/**
 * The producer ships `original_quantity`/`clamped_to` when it silently reduced
 * the cart line to the threshold (customer asked for 30, cart shows 12).
 * Returns null when the fields are absent or nothing was actually capped.
 */
const getCappedQuantity = (
  item: QuantityEscalationItem
): CappedQuantity | null => {
  const { original_quantity: original, clamped_to: cappedTo } = item;
  if (typeof original !== 'number' || typeof cappedTo !== 'number') {
    return null;
  }
  if (original <= cappedTo) {
    return null;
  }
  return { originalQuantity: original, cappedTo };
};

const formatCappedQuantityFlag = ({
  originalQuantity,
  cappedTo,
}: CappedQuantity): string =>
  `Customer asked for ${originalQuantity} — capped at ${cappedTo}`;

/** DOM id of a rendered transcript line, shared by the panel (scroll source) and the transcript (target). */
const transcriptMessageDomId = (messageId: string): string =>
  `transcript-msg-${messageId}`;

// Scroll target for a capped cart line — the attribute is set by CartItemReview
// only while the line is flagged, and queried by the panel's quantity card, so
// the two sides share the name here.
const CAPPED_CART_ITEM_ATTR = 'data-cart-item-id';
const cappedCartItemSelector = (cartItemId: string): string =>
  `[${CAPPED_CART_ITEM_ATTR}="${CSS.escape(cartItemId)}"]`;

export {
  NOT_ON_MENU_TOKEN_PREFIX,
  COUPON_NOT_ON_MENU_TOKEN,
  isNotOnMenuToken,
  isCouponToken,
  prettifyUnresolvedItemName,
  normalizeItemName,
  getCappedQuantity,
  formatCappedQuantityFlag,
  transcriptMessageDomId,
  CAPPED_CART_ITEM_ATTR,
  cappedCartItemSelector,
};
